/**
 * Feature Registry
 *
 * Central registry for checkout feature modules.
 */

import type { MarketContext } from '../markets/types';
import type { FeatureAvailability, FeatureModule } from './types';

/**
 * Feature Registry Class
 */
class FeatureRegistry {
  private features: Map<string, FeatureModule> = new Map();

  /**
   * Register a feature module
   */
  register(feature: FeatureModule): void {
    this.features.set(feature.id, feature);
  }

  /**
   * Register multiple features
   */
  registerMany(features: FeatureModule[]): void {
    for (const feature of features) {
      this.register(feature);
    }
  }

  /**
   * Get a feature by ID
   */
  get(id: string): FeatureModule | undefined {
    return this.features.get(id);
  }

  /**
   * Check if a feature is registered
   */
  has(id: string): boolean {
    return this.features.has(id);
  }

  /**
   * Get all registered features
   */
  getAll(): FeatureModule[] {
    return Array.from(this.features.values());
  }

  /**
   * Get registered feature IDs
   */
  getIds(): string[] {
    return Array.from(this.features.keys());
  }

  /**
   * Get available features for a context
   */
  getAvailable(context: {
    marketContext: MarketContext;
    lineItems?: Array<{ variantId: string; productType?: string }>;
  }): Array<{ feature: FeatureModule; availability: FeatureAvailability }> {
    const { marketContext, lineItems } = context;
    const available: Array<{ feature: FeatureModule; availability: FeatureAvailability }> = [];

    for (const feature of this.features.values()) {
      // Check module activation if required
      if (feature.activationModule) {
        if (!marketContext.isModuleActive(feature.activationModule)) {
          continue;
        }
      }

      // Check feature's own availability
      const availability = feature.checkAvailability({
        marketContext,
        lineItems,
      });

      if (availability.available) {
        available.push({ feature, availability });
      }
    }

    return available;
  }

  /**
   * Get features by category
   */
  getByCategory(category: string): FeatureModule[] {
    return Array.from(this.features.values()).filter((f) => f.category === category);
  }

  /**
   * Clear all registered features (for testing)
   */
  clear(): void {
    this.features.clear();
  }
}

/**
 * Global feature registry instance
 */
export const featureRegistry = new FeatureRegistry();

/**
 * Get feature (convenience function)
 */
export function getFeature(id: string): FeatureModule | undefined {
  return featureRegistry.get(id);
}

/**
 * Get available features (convenience function)
 */
export function getAvailableFeatures(context: {
  marketContext: MarketContext;
  lineItems?: Array<{ variantId: string; productType?: string }>;
}): Array<{ feature: FeatureModule; availability: FeatureAvailability }> {
  return featureRegistry.getAvailable(context);
}
